/**
 * Which model does what (Nexus Open, 8c). Per task (assistant, writing help,
 * tasks from text, daily summary, plugin builder) you choose a provider you
 * added yourself: Anthropic, OpenAI-compatible (OpenAI, Gemini, OpenRouter,
 * Mistral, Groq, Azure…) or a local server (Ollama, LM Studio), called straight
 * from this device. Keys stay on this device, never as plain text: in the OS
 * keychain on the desktop, AES-GCM-encrypted (see browserKeyStore.ts) for
 * this tab only in the browser. There is no shared/server-paid model --
 * every user brings their own.
 */
import { invoke } from '@tauri-apps/api/core';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  AiError,
  directTransport,
  isLocalUrl,
  PRESETS,
  type AiMode,
  type AiRequest,
  type ProviderKind,
} from '@nexus/ai';
import { isTauri } from '@nexus/store/tauri';
import { decryptFromStorage, encryptForStorage } from './browserKeyStore';

export const AI_TASKS: AiMode[] = ['chat', 'write', 'extract', 'summary', 'plugin'];

export interface ProviderSetting {
  id: string;
  /** The preset it was made from (anthropic, openai, ollama, custom, …). */
  preset: string;
  name: string;
  kind: ProviderKind;
  baseUrl?: string;
  models: string[];
  /** The models can call tools (the assistant's actions, the plugin builder). */
  tools: boolean;
  /** US dollars per million tokens, input and output (optional; local is free). */
  price?: [number, number];
}

/** A provider id (from `providers`) and one of its models. */
export interface Route {
  provider: string;
  model: string;
}

interface AiProvidersState {
  providers: ProviderSetting[];
  routes: Partial<Record<AiMode, Route>>;
  /** Which providers have a key stored (the keys themselves are never here). */
  keyed: Record<string, boolean>;
  add: (preset: string) => ProviderSetting;
  update: (id: string, patch: Partial<Omit<ProviderSetting, 'id'>>) => void;
  remove: (id: string) => void;
  setRoute: (task: AiMode, route: Route | null) => void;
}

export const useAiProviders = create<AiProvidersState>()(
  persist(
    (set, get) => ({
      providers: [],
      routes: {},
      keyed: {},
      add: (presetId) => {
        const preset = PRESETS.find((p) => p.id === presetId) ?? PRESETS.at(-1)!;
        const taken = new Set(get().providers.map((p) => p.id));
        let id = preset.id;
        for (let n = 2; taken.has(id); n++) id = `${preset.id}-${n}`;
        const provider: ProviderSetting = {
          id,
          preset: preset.id,
          name: preset.id,
          kind: preset.kind,
          ...(preset.baseUrl ? { baseUrl: preset.baseUrl } : {}),
          models: [...preset.models],
          tools: true,
        };
        set((s) => ({ providers: [...s.providers, provider] }));
        return provider;
      },
      update: (id, patch) =>
        set((s) => ({ providers: s.providers.map((p) => (p.id === id ? { ...p, ...patch } : p)) })),
      remove: (id) => {
        void deleteKey(id);
        set((s) => ({
          providers: s.providers.filter((p) => p.id !== id),
          routes: Object.fromEntries(
            Object.entries(s.routes).filter(([, r]) => r?.provider !== id),
          ) as AiProvidersState['routes'],
          keyed: Object.fromEntries(Object.entries(s.keyed).filter(([k]) => k !== id)),
        }));
      },
      setRoute: (task, route) =>
        set((s) => {
          const routes = { ...s.routes };
          if (route) routes[task] = route;
          else delete routes[task];
          return { routes };
        }),
    }),
    {
      name: 'nexus-open-ai',
      partialize: (s) => ({ providers: s.providers, routes: s.routes, keyed: s.keyed }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Keys

const SESSION_PREFIX = 'nexus-open-key:';

/** Desktop: the OS keychain. Browser: AES-GCM via browserKeyStore -- never plain text. */
export async function getKey(id: string): Promise<string | null> {
  if (isTauri()) return invoke<string | null>('secret_get', { id });
  try {
    const stored = sessionStorage.getItem(SESSION_PREFIX + id);
    return stored ? await decryptFromStorage(stored) : null;
  } catch {
    return null;
  }
}

export async function setKey(id: string, value: string): Promise<void> {
  const key = value.trim();
  if (!key) return deleteKey(id);
  if (isTauri()) await invoke('secret_set', { id, value: key });
  else sessionStorage.setItem(SESSION_PREFIX + id, await encryptForStorage(key));
  useAiProviders.setState((s) => ({ keyed: { ...s.keyed, [id]: true } }));
}

export async function deleteKey(id: string): Promise<void> {
  if (isTauri()) await invoke('secret_delete', { id }).catch(() => {});
  else
    try {
      sessionStorage.removeItem(SESSION_PREFIX + id);
    } catch {
      // nothing stored
    }
  useAiProviders.setState((s) => ({ keyed: { ...s.keyed, [id]: false } }));
}

/** In the browser a key lasts as long as the tab: check what is really there. */
export function refreshKeyed(): void {
  if (isTauri()) return;
  const { providers } = useAiProviders.getState();
  const keyed: Record<string, boolean> = {};
  for (const p of providers) {
    try {
      keyed[p.id] = !!sessionStorage.getItem(SESSION_PREFIX + p.id);
    } catch {
      keyed[p.id] = false;
    }
  }
  useAiProviders.setState({ keyed });
}

export const needsKey = (p: ProviderSetting): boolean =>
  p.kind === 'anthropic' || (!isLocalUrl(p.baseUrl) && p.preset !== 'custom');

// ---------------------------------------------------------------------------
// Routing

/** The route for a task: chosen, else the first provider with a model. */
export function routeFor(task: AiMode, state = useAiProviders.getState()): Route | null {
  const chosen = state.routes[task];
  if (chosen && state.providers.some((p) => p.id === chosen.provider)) return chosen;
  const first = state.providers.find((p) => p.models.length > 0);
  return first ? { provider: first.id, model: first.models[0]! } : null;
}

export type Readiness = 'ready' | 'none' | 'key' | 'no-tools';

function readiness(
  task: AiMode,
  state: AiProvidersState,
): { status: Readiness; route: Route | null; provider?: ProviderSetting } {
  const route = routeFor(task, state);
  if (!route) return { status: 'none', route };
  const provider = state.providers.find((p) => p.id === route.provider)!;
  if (needsKey(provider) && !state.keyed[provider.id]) return { status: 'key', route, provider };
  if (task === 'plugin' && !provider.tools) return { status: 'no-tools', route, provider };
  return { status: 'ready', route, provider };
}

/** Can this task run now, and with what (for the UI). */
export function useAiReady(task: AiMode) {
  const state = useAiProviders();
  return readiness(task, state);
}

/** A short name for the model a task uses ("llama3.2 · ollama"). */
export function routeLabel(route: Route | null): string {
  if (!route) return '';
  const p = useAiProviders.getState().providers.find((x) => x.id === route.provider);
  return p ? `${route.model} · ${p.name}` : route.model;
}

// ---------------------------------------------------------------------------
// Cost of calls from this device (a local estimate only)

const USAGE_KEY = 'nexus-open-ai-usage';
const month = () => new Date().toISOString().slice(0, 7);

export function directSpend(): number {
  try {
    const u = JSON.parse(localStorage.getItem(USAGE_KEY) ?? 'null') as {
      month: string;
      usd: number;
    } | null;
    return u?.month === month() ? u.usd : 0;
  } catch {
    return 0;
  }
}

function book(costUsd: number) {
  const usd = directSpend() + costUsd;
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify({ month: month(), usd }));
  } catch {
    // private window: the estimate is not kept
  }
  return { monthUsd: usd, limitUsd: 0 };
}

// ---------------------------------------------------------------------------
// Transport

/** The desktop app fetches outside the webview, so local servers need no CORS setup. */
async function appFetch(): Promise<typeof fetch | undefined> {
  if (!isTauri()) return undefined;
  const { fetch: tauriFetch } = await import('@tauri-apps/plugin-http');
  return tauriFetch as unknown as typeof fetch;
}

/** Sends a request to the model chosen for its task. */
export async function transport(req: AiRequest, signal?: AbortSignal): Promise<Response> {
  const state = useAiProviders.getState();
  const route = routeFor(req.mode, state);
  if (!route) throw new AiError('not-configured', 'no-model');
  const provider = state.providers.find((p) => p.id === route.provider)!;
  const apiKey = (await getKey(provider.id)) ?? '';
  if (needsKey(provider) && !apiKey) throw new AiError('bad-key', 'no-key');
  const doFetch = await appFetch();
  return directTransport({
    kind: provider.kind,
    ...(provider.baseUrl ? { baseUrl: provider.baseUrl } : {}),
    apiKey,
    model: route.model,
    tools: provider.tools,
    ...(provider.price ? { price: provider.price } : {}),
    ...(doFetch ? { fetch: doFetch } : {}),
    account: book,
  })(req, signal);
}

/** Where the notes the AI reads go, for the privacy line in the settings. */
export function destination(p: ProviderSetting): { local: boolean; host: string } {
  if (p.kind === 'anthropic') return { local: false, host: 'api.anthropic.com' };
  try {
    return { local: isLocalUrl(p.baseUrl), host: new URL(p.baseUrl ?? '').host };
  } catch {
    return { local: false, host: '' };
  }
}
